import { Box, Button, Paper, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useColorMode } from "src/design/ColorModeProvider";
import { stationControl, stationTile } from "src/design/stationControl";
import { elevation, radius } from "src/design/tokens";

export interface StudentTileProps {
  name: string;
  /** Planned modification label shown as a small chip, when the student has one. */
  plannedLabel?: string;
  /** Recorded value text; when present the tile is read-only. */
  recordedText?: string;
  /** Count events show the big count with +1 and Undo; timed events pass `count` undefined. */
  count?: number;
  onPlus?: () => void;
  onUndo?: () => void;
  /** Value field, mode toggle and modification controls. */
  children?: ReactNode;
}

const srOnly = {
  position: "absolute",
  width: "1px",
  height: "1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
} as const;

/** One student at a web station: name, planned modification, count and the +1 / Undo controls. */
export const StudentTile = ({
  name,
  plannedLabel,
  recordedText,
  count,
  onPlus,
  onUndo,
  children,
}: StudentTileProps) => {
  const { t } = useTranslation();
  const { mode } = useColorMode();
  return (
    <Paper
      component="li"
      sx={{
        ...stationTile,
        listStyle: "none",
        borderRadius: "22px",
        p: 2.25,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          component="h2"
          sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}
        >
          {name}
        </Typography>
        {plannedLabel ? (
          <Box
            component="span"
            sx={(theme) => ({
              px: 1.25,
              py: 0.75,
              borderRadius: `${radius.pill}px`,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: theme.palette.warning.light,
              color: theme.palette.warning.main,
              whiteSpace: "nowrap",
            })}
          >
            {plannedLabel}
          </Box>
        ) : null}
      </Box>
      {recordedText ? (
        <Typography
          role="status"
          sx={(theme) => ({ color: theme.palette.successInk, fontWeight: 700 })}
        >
          {recordedText}
        </Typography>
      ) : (
        <>
          {count !== undefined ? (
            <>
              <Typography
                variant="stat"
                component="p"
                aria-live="polite"
                sx={{ fontSize: 72, textAlign: "center", m: 0 }}
              >
                <Box component="span" sx={srOnly}>
                  {`${name}: `}
                </Box>
                {count}
              </Typography>
              <Button
                variant="contained"
                onClick={onPlus}
                aria-label={t("station.plusOneFor", {
                  name,
                  defaultValue: "+1 for {{name}}",
                })}
                sx={(theme) => ({
                  ...stationControl,
                  minHeight: 96,
                  borderRadius: "18px",
                  fontSize: 40,
                  fontWeight: 900,
                  // Design: the counter is the blue control, not the red page action.
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  boxShadow: elevation.glowPresidential[mode],
                  "&:hover": {
                    bgcolor: theme.palette.primary.main,
                    filter: "brightness(0.95)",
                  },
                  "&:active": { transform: "scale(.97)" },
                })}
              >
                +1
              </Button>
              <Button
                variant="outlined"
                onClick={onUndo}
                disabled={count === 0}
                aria-label={t("station.undoFor", {
                  name,
                  defaultValue: "Undo for {{name}}",
                })}
                sx={{
                  ...stationControl,
                  borderRadius: "14px",
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                {t("common.undo")}
              </Button>
            </>
          ) : null}
          {children}
        </>
      )}
    </Paper>
  );
};

export default StudentTile;
